import {
	AccountBookmarkModel,
	AccountModel,
	AccountOptionModel,
	AccountTopicPreference,
	AdvisorModel,
	AI_RESPONSE_SOURCE_IDS,
	ALERT_FREQUENCY,
	BannerMessage,
	BOOKMARK_TYPE,
	FormOption,
	PaginatedResponseModel,
	ProgramModel,
	TimezoneOptionModel,
} from '@/lib/models';
import { httpSingleton } from '@/lib/http-client';
import { constructUrl } from '@/lib/utils';

interface UpdateCurrentAccountRequestBody {
	name: string;
	title: string;
	userLevelTagId: string;
	userFunctionTagIds: string[];
	emailAddress?: string;
	bookingsUrl?: string;
	mobileNumber?: string;
	location?: string;
	timeZone?: string;
}

export interface AlertPreferencesResponseBody {
	announcementsPreference: boolean;
	authorsPreference: boolean;
	eventsPreference: boolean;
	authors: AdvisorModel[];
	frequency: ALERT_FREQUENCY | null;
	newTopics: AccountTopicPreference[];
	performanceStudyPreference: boolean;
	topicPreference: boolean;
	topicPreferences: AccountTopicPreference[];
}

export interface AlertPreferencesRequestBody {
	announcementsPreference: boolean;
	authorsPreference: boolean;
	eventsPreference: boolean;
	followedAuthors: string[];
	frequency: ALERT_FREQUENCY | null;
	performanceStudyPreference: boolean;
	topics: string[];
	topicPreference: boolean;
}

export interface AiPreferencesRequestBody {
	shouldAnimate: boolean;
	showFollowUpQuestions: boolean;
	aiResponseSourceIds: AI_RESPONSE_SOURCE_IDS[];
}

export const accountService = {
	getCurrentAccount() {
		return httpSingleton.orchestrateRequest<{ account: AccountModel }>({
			method: 'GET',
			url: `/advisory-service/api/v1/account`,
		});
	},
	updateCurrentAccount(id: string, data: UpdateCurrentAccountRequestBody) {
		return httpSingleton.orchestrateRequest<{ account: AccountModel }>({
			method: 'PUT',
			url: `/advisory-service/api/v1/account/${id}`,
			data,
		});
	},
	updateCurrentAccountAiPreferences(accountId: string, data: AiPreferencesRequestBody) {
		return httpSingleton.orchestrateRequest<{ account: AccountModel }>({
			method: 'PUT',
			url: `/advisory-service/api/v1/account/${accountId}/ai-preferences`,
			data,
		});
	},
	getAccountOptions(option: 'USER_LEVEL' | 'USER_FUNCTION') {
		return httpSingleton.orchestrateRequest<{ options: AccountOptionModel[] }>({
			method: 'GET',
			url: `/advisory-service/api/v1/account/options/${option}`,
		});
	},
	getPrograms(accountId: string, queryParameters?: { activeOnly?: boolean }) {
		return httpSingleton.orchestrateRequest<{ programs: ProgramModel[] }>({
			method: 'GET',
			url: constructUrl(`/advisory-service/api/v1/account/${accountId}/programs`, queryParameters),
		});
	},
	getAccountFilters() {
		return httpSingleton.orchestrateRequest<{ sortFields: FormOption[] }>({
			method: 'GET',
			url: '/advisory-service/api/v1/account-filter',
		});
	},
	getFreshdeskSupportUrl() {
		return httpSingleton.orchestrateRequest<{ freshdeskSupportUrl: string }>({
			method: 'GET',
			url: '/advisory-service/api/v1/account/freshdesk-support',
		});
	},
	getFreshdeskTicketsUrl() {
		return httpSingleton.orchestrateRequest<{ freshdeskSupportUrl: string }>({
			method: 'GET',
			url: '/advisory-service/api/v1/account/freshdesk-tickets',
		});
	},
	getBannerMessages() {
		return httpSingleton.orchestrateRequest<{ bannerMessages: BannerMessage[] }>({
			method: 'GET',
			url: '/advisory-service/api/v1/account/banner-messages',
		});
	},
	markBannerMessageRead(bannerMessageId: string) {
		return httpSingleton.orchestrateRequest<{ bannerMessages: BannerMessage[] }>({
			method: 'PUT',
			url: `/advisory-service/api/v1/account/banner-message/${bannerMessageId}`,
		});
	},
	trackSignIn() {
		return httpSingleton.orchestrateRequest<any>({
			method: 'PUT',
			url: '/advisory-service/api/v1/account/track-login',
		});
	},
	updateAccountBookmarkContent(
		accountId: string,
		data: {
			bookmarkId?: string;
			bookmarkType: BOOKMARK_TYPE;
			contentId: string;
			activeFlag: boolean;
		}
	) {
		return httpSingleton.orchestrateRequest<{ bookmark: AccountBookmarkModel }>({
			method: 'POST',
			url: `/advisory-service/api/v1/account/${accountId}/bookmarks`,
			data,
		});
	},
	getAccountBookmarks(
		accountId: string,
		queryParameters?: {
			page?: number;
			size?: number;
		}
	) {
		return httpSingleton.orchestrateRequest<PaginatedResponseModel & { bookmarks: AccountBookmarkModel[] }>({
			method: 'GET',
			url: constructUrl(`/advisory-service/api/v1/account/${accountId}/bookmarks`, queryParameters),
		});
	},
	getAlertPreferences() {
		return httpSingleton.orchestrateRequest<AlertPreferencesResponseBody>({
			method: 'GET',
			url: '/advisory-service/api/v1/account/content-alert-preferences',
		});
	},
	updateAlertPreferences(data: AlertPreferencesRequestBody) {
		return httpSingleton.orchestrateRequest<AlertPreferencesResponseBody>({
			method: 'PUT',
			url: '/advisory-service/api/v1/account/content-alert-preferences',
			data,
		});
	},
	getTimezones() {
		return httpSingleton.orchestrateRequest<{
			timezones: TimezoneOptionModel[];
		}>({
			method: 'GET',
			url: '/advisory-service/api/v1/account/timezones',
		});
	},
};
